export type ResultDestination = "new-tab" | "same-window"

type BrowserNav = {
  open: typeof window.open
  assign: (href: string) => void
}

const defaultNav: BrowserNav = {
  open: (url, target, features) => window.open(url, target, features),
  assign: (href) => {
    window.location.assign(href)
  },
}

/**
 * Opens a result URL.
 *
 * `reservedTab` is a blank window opened in the click turn, before property
 * search awaits a recent-search write. `window.open` after that await is
 * blocked, so the blank tab is navigated instead. A blocked popup falls back
 * to the same window so the selection is not dropped.
 */
export function openResult(
  href: string,
  openInNewTab: boolean,
  reservedTab: Window | null,
  nav: BrowserNav = defaultNav
): ResultDestination {
  let tab = reservedTab && !reservedTab.closed ? reservedTab : null

  if (openInNewTab && !tab) {
    tab = nav.open("about:blank", "_blank")
  }

  if (openInNewTab && tab && !tab.closed) {
    // about:blank is still same-origin, so this clears the opener. After
    // replace() the result origin would reject the assignment.
    try {
      tab.opener = null
    } catch {
      // Already unreachable.
    }
    try {
      tab.location.replace(href)
    } catch {
      tab.close()
      nav.assign(href)
      return "same-window"
    }
    return "new-tab"
  }

  if (tab && !tab.closed) {
    tab.close()
  }

  nav.assign(href)
  return "same-window"
}

/** Blank tab opened synchronously, before any await, so the popup blocker allows it. */
export function reserveNewTab(): Window | null {
  return window.open("about:blank", "_blank")
}
